import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack } from '@mui/material';
import TopBar from "../../../../../component/TopBar";
import DataTable from "../../../../../component/DataTable";
import AddNewExpense from "./AddNewExpense";
import { expenseColumns } from "./expenseColumns";
import BtnPrimary from "../../../../../component/PrimaryButton";
import Counter from "../../../../../component/Counter";
import InlineMessage from "../../../../../component/InlineMessage";
import { counterRowSx } from "../../../container/frameLayout";
import { claimTotals, claimCountLabel } from "./claimFacts";
import { paymentTiming } from "./claimRules";

import { getEmployeeExpense } from "../../../../../services/expense";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../theme";

const money = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

const Expenses = () => {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [adding, setAdding] = useState(false);
  // Bumped when a claim is submitted, so the list reflects it.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getEmployeeExpense();
        if (res?.success === false) throw new Error(res?.message || "Failed to load expenses");
        if (active) setData((Array.isArray(res?.data) ? res.data : []).filter((e) => e.type === "ANY").reverse());
      } catch (error) {
        if (active) notify.error(error?.message || "Failed to load expenses");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => claimTotals(data), [data]);

  const recent = data.slice(0, 6);

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Expenses"} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Overview</Box>
        {/* A modal over the list, not a page away from it. */}
        <BtnPrimary onClick={() => setAdding(true)} sx={{ borderRadius: "8px" }}>Submit a claim</BtnPrimary>
      </Stack>

      <Box sx={{ ...counterRowSx(4), mb: "1.25rem" }}>
        <Counter
          label="Pending"
          value={loading ? "—" : money(totals.pending.amount)}
          sub={claimCountLabel(totals.pending.count, "nothing waiting")}
          tone={totals.pending.count ? tokens.warningText : undefined}
        />
        <Counter
          label="Approved, not yet paid"
          value={loading ? "—" : money(totals.awaiting.amount)}
          sub={totals.awaiting.count ? `${claimCountLabel(totals.awaiting.count)}, paid with salary` : "nothing outstanding"}
        />
        <Counter
          label={`Reimbursed ${totals.year}`}
          value={loading ? "—" : money(totals.reimbursed.amount)}
          sub={claimCountLabel(totals.reimbursed.count, "none this year")}
        />
        <Counter
          label={`Rejected ${totals.year}`}
          value={loading ? "—" : money(totals.rejected.amount)}
          sub={claimCountLabel(totals.rejected.count, "none this year")}
          tone={totals.rejected.count ? tokens.danger : undefined}
        />
      </Box>

      {/* How reimbursement actually reaches them. The single most common
          question this screen gets asked is "when do I get it", and the answer
          is not on the claim — it is a property of the payroll cycle. */}
      {!loading && (
        <InlineMessage tone="info" sx={{ mb: "1.5rem" }}>
          {paymentTiming(null).text}
        </InlineMessage>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1rem" }}>
        <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>Recent expenses</Box>
        <BtnPrimary to="/app/employee-expense/my-expense" sx={{ borderRadius: "8px" }}>View all</BtnPrimary>
      </Stack>

      <DataTable
        rows={recent}
        loading={loading}
        empty={{ title: "No expenses yet", description: "Submit an expense to see it here." }}
        tableSx={{ width: "100%", tableLayout: "fixed" }}
        columns={expenseColumns(money)}
      />

      {/* A modal over the list: submitting a claim is something you do while
          looking at your claims, and coming back to the list is the ending. */}
      <AddNewExpense
        open={adding}
        onClose={() => { setAdding(false); setReloadKey((k) => k + 1); }}
      />
    </Box>
  );
};

export default Expenses;
